import React from "react";

export interface TimezoneOption {
  value: string;
  label: string;
}

export interface TimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  isReadOnly?: boolean;
  className?: string;
  label?: string;
  description?: string;
  showLabel?: boolean;
  variant?: "default" | "compact";
}

// Default timezone options - IANA format (Region/City)
export const DEFAULT_TIMEZONE_OPTIONS: TimezoneOption[] = [
  {
    value: "America/Los_Angeles",
    label: "(GMT -08:00/-07:00) Pacific Time (PT) Los Angeles",
  },
  {
    value: "America/New_York",
    label: "(GMT -05:00/-04:00) Eastern Time (ET) New York",
  },
  {
    value: "America/Chicago",
    label: "(GMT -06:00/-05:00) Central Time (CT) Chicago",
  },
  {
    value: "America/Denver",
    label: "(GMT -07:00/-06:00) Mountain Time (MT) Denver",
  },
  {
    value: "America/Anchorage",
    label: "(GMT -09:00/-08:00) Alaska Time (AKT) Anchorage",
  },
  {
    value: "Pacific/Honolulu",
    label: "(GMT -10:00) Hawaii Standard Time (HST) Honolulu",
  },
  {
    value: "Pacific/Pago_Pago",
    label: "(GMT -11:00) Samoa Standard Time (SST) Pago Pago",
  },
];

const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value,
  onChange,
  isReadOnly = false,
  className = "",
  label = "Time Zone",
  description = "Required to configure all the time based on operations on your Number.",
  showLabel = true,
  variant = "default",
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  if (variant === "compact") {
    return (
      <div className={`relative ${className}`}>
        <select
          className="w-full p-3 bg-gray-50 border rounded-md appearance-none text-gray-700"
          value={value}
          onChange={handleChange}
          disabled={isReadOnly}
          aria-label="Select timezone"
        >
          {DEFAULT_TIMEZONE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-start justify-between mb-8 ${className}`}>
      {showLabel && (
        <div className="flex flex-col justify-start items-start gap-1">
          <div className="text-zinc-700 text-sm font-medium">{label}</div>
          <div className="text-gray-500 text-xs">{description}</div>
        </div>
      )}
      <div className="w-72 py-0.5 relative inline-flex flex-col justify-start items-end gap-0.5 overflow-hidden">
        <select
          className="self-stretch text-zinc-700 text-base font-normal bg-transparent border-none outline-none pr-8 appearance-none"
          value={value}
          onChange={handleChange}
          disabled={isReadOnly}
          aria-label="Select timezone"
        >
          {DEFAULT_TIMEZONE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="self-stretch h-0 origin-top-left -rotate-180 outline outline-1 outline-offset-[-0.50px] outline-zinc-300"></div>
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default TimezoneSelector;
